import { useEffect, useState } from "react";
import { clearFpsPending, loadFpsPending, type PendingFpsMatch } from "../lib/fpsReconnect";

interface FPSReconnectCardProps {
  /** Logged-in wallet — scopes the card so only the owning account sees it. */
  walletAddress: string | null;
  /** Asks the parent to relaunch the FPS iframe with this reconnection token. */
  onReconnect: (reconnectionToken: string) => void;
}

const ACCENT = "#ff5a3c";
const ACCENT2 = "#ff8a3c";

/**
 * Floating bottom-right card shown when the player has a live FPS Arena
 * match they can rejoin (token persisted in localStorage from the iframe's
 * "hupper-fps-active" message). Polls every 500 ms so a server-side
 * dispose (match_end / forfeit / TTL) clears it within a beat. Stacks
 * above the TD/Math cards.
 */
export default function FPSReconnectCard({ walletAddress, onReconnect }: FPSReconnectCardProps) {
  const [pending, setPending] = useState<PendingFpsMatch | null>(null);

  useEffect(() => {
    const refresh = () => setPending(loadFpsPending());
    refresh();
    const id = window.setInterval(refresh, 500);
    return () => window.clearInterval(id);
  }, []);

  if (!pending || !walletAddress) return null;
  if ((pending.walletAddress ?? "").toLowerCase() !== walletAddress.toLowerCase()) return null;

  const stakeDollars = Number(pending.stake) || 0;
  const totalPot = stakeDollars * 2;

  const handleClick = () => onReconnect(pending.reconnectionToken);
  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearFpsPending();
    setPending(null);
  };

  return (
    <div
      className="fixed right-5 z-[90] w-[336px] rounded-2xl overflow-hidden"
      style={{
        bottom: 20 + 2 * 168, // stack above the TD / Math cards
        background: "linear-gradient(160deg, rgba(26,26,26,0.97) 0%, rgba(15,15,15,0.97) 100%)",
        border: `1px solid ${ACCENT}55`,
        boxShadow: `0 12px 40px ${ACCENT}38, 0 0 0 1px ${ACCENT}15`,
        backdropFilter: "blur(14px)",
      }}
    >
      <div
        className="flex items-center justify-between px-4 py-2.5 border-b border-white/5"
        style={{ background: `linear-gradient(90deg, ${ACCENT}22 0%, transparent 70%)` }}
      >
        <div className="flex items-center gap-2">
          <span
            className="flex items-center justify-center w-7 h-7 rounded-lg text-[14px] font-black"
            style={{ background: `${ACCENT}25`, color: ACCENT }}
          >
            ◎
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] font-bold tracking-wider text-white/95 uppercase">
              FPS Arena
            </span>
            <span className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-white/40">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live match
            </span>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-white/30 hover:text-white/70 text-[11px] uppercase tracking-wider px-1 transition"
          title="Forfeit and dismiss"
        >
          ✕
        </button>
      </div>

      <div className="px-4 py-4 flex items-center gap-4">
        <button
          onClick={handleClick}
          className="relative w-[58px] h-[58px] flex-shrink-0 group focus:outline-none"
          aria-label="Reconnect"
        >
          <span
            className="absolute inset-1.5 rounded-full flex items-center justify-center text-white font-bold text-[12px] tracking-wider transition-transform group-hover:scale-105 group-active:scale-95"
            style={{
              background: `linear-gradient(140deg, ${ACCENT} 0%, ${ACCENT2} 100%)`,
              boxShadow: `0 4px 14px ${ACCENT}66`,
            }}
          >
            JOIN
          </span>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5 mb-1">
            <span className="text-2xl font-extrabold text-white tracking-tight">
              ${totalPot.toFixed(2)}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">
              pot
            </span>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <span className="font-mono text-[11px] text-white/60 truncate">
              Rejoin before the grace runs out
            </span>
          </div>
          <button
            onClick={handleClick}
            className="w-full py-2 rounded-lg text-white text-xs font-bold uppercase tracking-wider transition-all hover:shadow-lg active:scale-[0.98]"
            style={{
              background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT2} 100%)`,
              boxShadow: `0 4px 14px ${ACCENT}40`,
            }}
          >
            Reconnect
          </button>
        </div>
      </div>
    </div>
  );
}
